/** Hook d'authentification - gère le flux login MFA en 2 étapes + le token JWT. */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import { createElement } from 'react';
import { authApi } from '../services/api';

interface AuthContextType {
  isAuthenticated: boolean;
  /**
   * Étape 1 : vérifie les identifiants et déclenche l'envoi du code MFA.
   * Retourne les infos du challenge à afficher (id, hint email).
   */
  requestCode: (username: string, password: string) => Promise<{
    challenge_id: string;
    email_hint: string;
    expires_in_seconds: number;
  }>;
  /** Étape 2 : valide le code reçu par email → authentifie. */
  verifyCode: (challenge_id: string, code: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(
    () => !!localStorage.getItem('token')
  );

  const requestCode = useCallback(async (username: string, password: string) => {
    return authApi.login(username, password);
  }, []);

  const verifyCode = useCallback(async (challenge_id: string, code: string) => {
    const { access_token } = await authApi.verify(challenge_id, code);
    localStorage.setItem('token', access_token);
    setIsAuthenticated(true);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setIsAuthenticated(false);
  }, []);

  return createElement(
    AuthContext.Provider,
    { value: { isAuthenticated, requestCode, verifyCode, logout } },
    children
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit être utilisé dans un AuthProvider');
  }
  return context;
}
